/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { ArrowPathIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";

/** Placeholder rows shown while a list loads (instead of a bare "loading…"). */
export const SkeletonRows: React.FC<{ rows?: number; className?: string }> = ({ rows = 5, className }) => (
  <div className={`st-skel ${className ?? ""}`} aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="st-skel-row" style={{ animationDelay: `${i * 80}ms` }} />
    ))}
  </div>
);

/** First-run / nothing-here state with an optional call to action. */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}> = ({ icon, title, hint, action }) => (
  <div className="st-box">
    {icon && <div className="st-ico">{icon}</div>}
    <div className="st-title">{title}</div>
    {hint && <div className="st-hint">{hint}</div>}
    {action && <div className="st-act">{action}</div>}
  </div>
);

/** A failed request with a retry button. */
export const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="st-box st-box--error" role="alert">
    <div className="st-ico">
      <ExclamationCircleIcon className="h-7 w-7" />
    </div>
    <div className="st-title">Something went wrong</div>
    <div className="st-hint">{message}</div>
    {onRetry && (
      <div className="st-act">
        <button type="button" className="gh-btn" onClick={onRetry}>
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    )}
  </div>
);
